import React from 'react';
import { useGameStore } from '../../state/game-store.js';
import { networkClient } from '../../network/network-client.js';
import { soundSystem } from '../../audio/sound-system.js';
import { Play, RotateCcw, Settings, LogOut } from 'lucide-react';

interface PauseMenuProps {
  onOpenSettings: () => void;
}

export const PauseMenu: React.FC<PauseMenuProps> = ({ onOpenSettings }) => {
  const isPaused = useGameStore((s) => s.isPaused);
  const setPaused = useGameStore((s) => s.setPaused);
  const gameMode = useGameStore((s) => s.gameMode);
  const setGameMode = useGameStore((s) => s.setGameMode);
  const resetGame = useGameStore((s) => s.resetGame);

  if (!isPaused || gameMode === 'menu') return null;

  const handleResume = () => {
    soundSystem.playUiClick();
    setPaused(false);
  };

  const handleRestart = () => {
    soundSystem.playUiClick();
    resetGame();
    setPaused(false);
  };

  const handleQuitToMenu = () => {
    soundSystem.playUiClick();
    if (gameMode === 'multiplayer') {
      networkClient.disconnect();
    }
    setGameMode('menu');
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 font-mono">
      <div className="hud-panel max-w-sm w-full p-6 rounded-lg border border-emerald-800/60 space-y-5 text-center">
        <h2 className="text-xl font-bold tracking-widest text-zinc-100 uppercase border-b border-zinc-800 pb-3">
          Tactical Pause
        </h2>

        <div className="space-y-3">
          <button
            onClick={handleResume}
            className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-zinc-950 font-bold rounded flex items-center justify-center gap-2 text-xs uppercase tracking-wider transition-all"
          >
            <Play className="w-4 h-4 fill-current" /> Resume Engagement
          </button>

          {gameMode === 'singleplayer' && (
            <button
              onClick={handleRestart}
              className="w-full py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-200 font-bold rounded flex items-center justify-center gap-2 text-xs uppercase tracking-wider transition-all"
            >
              <RotateCcw className="w-4 h-4" /> Restart Scenario
            </button>
          )}

          <button
            onClick={() => {
              soundSystem.playUiClick();
              onOpenSettings();
            }}
            className="w-full py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-200 font-bold rounded flex items-center justify-center gap-2 text-xs uppercase tracking-wider transition-all"
          >
            <Settings className="w-4 h-4" /> Control Settings
          </button>

          <button
            onClick={handleQuitToMenu}
            className="w-full py-2.5 bg-rose-950/40 hover:bg-rose-900/60 border border-rose-800/60 text-rose-300 font-bold rounded flex items-center justify-center gap-2 text-xs uppercase tracking-wider transition-all"
          >
            <LogOut className="w-4 h-4" /> Abandon Mission
          </button>
        </div>
      </div>
    </div>
  );
};
